import { RedirectToSignIn, useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { toast, Input, Label, Textarea, Button } from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  RdComboBox,
  type RdPickerEntry,
  type RdComboBoxValue,
} from "@/components/v2/rd-combobox";
import { NotProvisioned } from "@/components/not-provisioned";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { cn } from "@/lib/utils";
import { can } from "@/lib/permissions";
import { todayIsoDate } from "../../../shared/local-date";
import type { AlwaysAiUser } from "../../../shared/api-types";

// Local port of legacy lib/v2/transcript-limits.ts.
const MAX_TRANSCRIPT_CHARS = 200_000;

function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    if (body?.error) return body.error;
  }

  return "Something went wrong. Please try again.";
}

// Client-side port of legacy transcripts/upload/page.tsx: withPageAuth becomes
// the sign-in redirect + /api/me provisioning gate, and the role-scoped
// rds/supervisors load becomes GET /api/rds?include=supervisors.
export function UploadWorkspace(): ReactElement | null {
  const { getToken, isLoaded, isSignedIn } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const user = useQuery({
    queryKey: ["current-user"],
    enabled: isLoaded && isSignedIn,
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
  const currentUser = user.data?.user;
  const picker = useQuery({
    queryKey: ["rd-picker"],
    enabled: Boolean(currentUser),
    queryFn: () =>
      authenticatedApi<{ data: { rds: RdPickerEntry[] } }>(
        getToken,
        "/api/rds?include=supervisors"
      ),
  });

  if (isLoaded && !isSignedIn) return <RedirectToSignIn />;

  if (user.isError) {
    if (user.error instanceof ApiRequestError && user.error.status === 403) {
      return <NotProvisioned />;
    }

    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{apiErrorMessage(user.error)}</p>
      </div>
    );
  }

  if (!currentUser) return null;

  if (picker.isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{apiErrorMessage(picker.error)}</p>
      </div>
    );
  }

  if (!picker.data) return null;

  const { rds } = picker.data.data;

  return (
    <div className="max-w-[1344px] mx-auto px-6 py-8">
      <BreadcrumbSetter segments={[{ label: "Transcripts" }, { label: "Upload" }]} />
      <TranscriptUploadClient
        rds={rds}
        canChooseSupervisor={
          can(currentUser, "rds:create:all") || can(currentUser, "rds:create:mapped")
        }
        currentUserId={currentUser.id}
      />
    </div>
  );
}

interface TranscriptUploadClientProps {
  rds: RdPickerEntry[];
  canChooseSupervisor: boolean;
  currentUserId: string;
}

const FIELD_BASE =
  "h-9 rounded-[10px] bg-background border-border text-foreground text-body md:text-body px-3 placeholder:text-subtle shadow-none";
const LABEL_BASE =
  "text-caption font-semibold uppercase tracking-wider text-muted-foreground";

type GetToken = Parameters<typeof authenticatedApi>[0];

interface TranscriptUpload {
  transcript: string;
  clientName: string;
  sessionDate: string;
}

/** POSTs the session; resolves to the error to show, or null on success. */
async function postTranscript(
  getToken: GetToken,
  rdId: string,
  upload: TranscriptUpload
): Promise<string | null> {
  try {
    await authenticatedApi(getToken, `/api/rds/${rdId}/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        transcript: upload.transcript,
        clientName: upload.clientName.trim() || null,
        sessionDate: upload.sessionDate || null,
      }),
    });
  } catch (requestError) {
    if (!(requestError instanceof ApiRequestError)) throw requestError;

    const body = (requestError.body ?? {}) as { error?: string };

    return body.error ?? "Failed to upload transcript.";
  }

  return null;
}

function announceUpload(picked: RdPickerEntry | undefined): void {
  const supervisorName = picked?.supervisorName;

  toast.success(`Uploaded for ${picked ? picked.name : "RD"}`, {
    description: supervisorName
      ? `${supervisorName} will see it on their RD page.`
      : "The supervisor will see it on their RD page.",
  });
}

function useUploadFields() {
  const [rd, setRd] = useState<RdComboBoxValue | null>(null);
  const [clientName, setClientName] = useState("");
  const [sessionDate, setSessionDate] = useState(todayIsoDate);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setRd(null);
    setClientName("");
    setSessionDate(todayIsoDate());
    setTranscript("");
    setError(null);
  }

  return {
    rd,
    setRd,
    clientName,
    setClientName,
    sessionDate,
    setSessionDate,
    transcript,
    setTranscript,
    error,
    setError,
    resetForm,
  };
}

type UploadFields = ReturnType<typeof useUploadFields>;

function transcriptLengthError(trimmed: string): string | null {
  if (trimmed.length <= MAX_TRANSCRIPT_CHARS) return null;

  return `Transcript exceeds maximum length of ${MAX_TRANSCRIPT_CHARS.toLocaleString()} characters.`;
}

function useUploadSubmit(fields: UploadFields, rds: RdPickerEntry[]) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { rd, transcript, setError } = fields;
  const canSubmit = rd !== null && transcript.trim().length > 0 && !isSubmitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!rd || !canSubmit) return;

    const trimmed = transcript.trim();
    if (!trimmed) return;

    const lengthError = transcriptLengthError(trimmed);

    if (lengthError) {
      setError(lengthError);

      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const failure = await postTranscript(getToken, rd.rdId, {
        transcript: trimmed,
        clientName: fields.clientName,
        sessionDate: fields.sessionDate,
      });

      if (failure) {
        setError(failure);

        return;
      }

      announceUpload(rds.find((r) => r.id === rd.rdId));
      // Legacy relied on the next server render for fresh picker data; here the
      // picker is client-cached, so refresh it.
      void queryClient.invalidateQueries({ queryKey: ["rd-picker"] });
      fields.resetForm();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return { isSubmitting, canSubmit, handleSubmit };
}

function TranscriptUploadClient({
  rds,
  canChooseSupervisor,
  currentUserId,
}: TranscriptUploadClientProps) {
  const fields = useUploadFields();
  const { isSubmitting, canSubmit, handleSubmit } = useUploadSubmit(fields, rds);
  const rdPicked = fields.rd !== null;

  return (
    <div className="flex flex-col items-center">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[560px] flex flex-col gap-[14px] rounded-2xl bg-card border border-border shadow-[0_10px_24px_rgba(40,3,15,0.08)] py-[18px] px-5"
      >
        <UploadHeading />

        <RdComboBox
          rds={rds}
          value={fields.rd}
          onChange={(v) => {
            fields.setRd(v);
            fields.setError(null);
          }}
          canChooseSupervisor={canChooseSupervisor}
          defaultSupervisorId={currentUserId}
          autoFocus
          disabled={isSubmitting}
        />

        <UploadDetailFields
          fields={fields}
          rdPicked={rdPicked}
          isSubmitting={isSubmitting}
        />
        <UploadTranscriptField
          fields={fields}
          rdPicked={rdPicked}
          isSubmitting={isSubmitting}
        />

        {fields.error && (
          <div className="rounded-lg border border-error/20 bg-error/5 px-3 py-2 text-sm text-error">
            <p>{fields.error}</p>
          </div>
        )}

        <UploadFooter
          onCancel={fields.resetForm}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
        />
      </form>
    </div>
  );
}

function UploadHeading() {
  return (
    <div className="flex items-center gap-[10px]">
      <div className="flex items-center justify-center h-[30px] w-[30px] rounded-lg bg-ai-soft shrink-0">
        <Plus className="h-[15px] w-[15px] text-ai" strokeWidth={2.5} />
      </div>
      <div>
        <h3 className="font-sans text-title-sm font-semibold leading-[18px] text-foreground">
          Upload a transcript
        </h3>
        <p className="text-caption text-muted-foreground mt-[2px]">
          Pick the RD this session belongs to. Supervisor is inferred.
        </p>
      </div>
    </div>
  );
}

interface UploadFieldProps {
  fields: UploadFields;
  rdPicked: boolean;
  isSubmitting: boolean;
}

function UploadDetailFields({ fields, rdPicked, isSubmitting }: UploadFieldProps) {
  return (
    <div className={cn("flex gap-3", !rdPicked && "opacity-50")}>
      <div className="flex flex-col gap-1 flex-[2] min-w-0">
        <Label htmlFor="upload-client-name" className={LABEL_BASE}>
          Client name (optional)
        </Label>
        <Input
          id="upload-client-name"
          value={fields.clientName}
          onChange={(e) => fields.setClientName(e.target.value)}
          placeholder="e.g. Joanna"
          disabled={isSubmitting || !rdPicked}
          className={FIELD_BASE}
        />
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <Label htmlFor="upload-session-date" className={LABEL_BASE}>
          Session date (optional)
        </Label>
        <Input
          id="upload-session-date"
          type="date"
          value={fields.sessionDate}
          onChange={(e) => fields.setSessionDate(e.target.value)}
          disabled={isSubmitting || !rdPicked}
          className={FIELD_BASE}
        />
      </div>
    </div>
  );
}

function UploadTranscriptField({ fields, rdPicked, isSubmitting }: UploadFieldProps) {
  return (
    <div className={cn("flex flex-col gap-1", !rdPicked && "opacity-50")}>
      <div className="flex items-center justify-between">
        <Label htmlFor="upload-transcript" className={LABEL_BASE}>
          Transcript
        </Label>
        <span className="text-caption text-subtle">
          {fields.transcript.length.toLocaleString()} characters
        </span>
      </div>
      <Textarea
        id="upload-transcript"
        value={fields.transcript}
        onChange={(e) => fields.setTranscript(e.target.value)}
        placeholder={
          rdPicked ? "Paste the session transcript here…" : "Pick an RD first…"
        }
        disabled={isSubmitting || !rdPicked}
        className="h-[180px] min-h-[180px] resize-none rounded-[10px] bg-background border-border py-3 px-[14px] font-mono text-caption md:text-caption text-muted-strong shadow-none"
      />
    </div>
  );
}

function UploadFooter({
  onCancel,
  isSubmitting,
  canSubmit,
}: {
  onCancel: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 pt-1">
      <p className="text-caption text-muted-foreground">
        Analysis runs in the background after you submit.
      </p>
      <div className="flex gap-[10px] shrink-0">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-9 rounded-[10px] px-4 text-body font-medium border-border"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          className="h-9 rounded-[10px] px-[18px] bg-primary text-primary-foreground hover:bg-primary-hover text-body font-medium shadow-sm"
        >
          {isSubmitting ? "Uploading…" : "Upload"}
        </Button>
      </div>
    </div>
  );
}
